import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Modal,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";
import EditProduct from "./ProductForm/EditProduct";
import ViewProduct from "./ProductForm/ViewProduct";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return null;
}

function normalize(text) {
  if (!text) return "";
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const AdminProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState(null);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const fetchProducts = async () => {
    setLoading(true);
    const r = await fetch("/productos/ver-productos/", { credentials: "include" });
    const d = await r.json();
    if (r.ok) setProducts(d.products || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const s = location.state && location.state.snackbar;
    if (s && s.message) {
      setSnackbarType(s.type || "success");
      setSnackbarMessage(s.message);
      setSnackbarOpen(true);
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const openDeleteModal = (id) => {
    setDeleteProductId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    const csrf = getCSRFToken();
    const r = await fetch(`/productos/eliminar-producto/${deleteProductId}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": csrf },
      credentials: "include",
    });
    if (r.ok) {
      fetchProducts();
      setSnackbarType("success");
      setSnackbarMessage("Producto eliminado correctamente");
      setSnackbarOpen(true);
    } else {
      setSnackbarType("error");
      setSnackbarMessage("No se pudo eliminar el producto");
      setSnackbarOpen(true);
    }
    setDeleteModalOpen(false);
  };

  const handleEditSubmit = async (payload) => {
    const csrf = getCSRFToken();
    const formData = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (k === "existingImages") v.forEach((i) => formData.append("keep_images", i));
      else if (k === "newImages") v.forEach((f) => formData.append("images", f));
      else formData.append(k, v);
    });
    const r = await fetch(`/productos/editar-producto/${selectedProduct.id}/`, {
      method: "POST",
      headers: { "X-CSRFToken": csrf },
      body: formData,
      credentials: "include",
    });
    if (r.ok) {
      setEditModalOpen(false);
      fetchProducts();
      setSnackbarType("success");
      setSnackbarMessage("Producto actualizado correctamente");
      setSnackbarOpen(true);
    } else {
      const d = await r.json().catch(() => ({}));
      setSnackbarType("error");
      setSnackbarMessage(d.error || "Error al actualizar");
      setSnackbarOpen(true);
    }
  };

  const term = normalize(searchTerm);
  const filteredProducts = products.filter((p) => {
    const n = normalize(p.name);
    const c = normalize(p.code);
    const b = normalize(p.barcode);
    return n.includes(term) || c.includes(term) || b.includes(term);
  });

  const handleSelect = (c) => {
    if (c === "AdminSection") navigate("/admin-panel/secciones");
    else if (c === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (c === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (c === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (c === "AdminProforma") navigate("/admin-panel/proformas");
    else if (c === "AdminClient") navigate("/admin-panel/clientes");
    else if (c === "AdminServices") navigate("/admin-panel/servicios");
    else if (c === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const openEdit = (p) => {
    setSelectedProduct(p);
    setEditModalOpen(true);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#edf0f7ff" }}>
      <SidebarMenu open={open} setOpen={setOpen} submenuOpen={submenuOpen} setSubmenuOpen={setSubmenuOpen} handleSelect={handleSelect} handleLogout={handleLogout} navigate={navigate} />

      <Box sx={{ flexGrow: 1, transition: "all 0.3s ease" }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: "none", backgroundColor: "#1565C0" }} onClick={() => navigate("nuevo")}>
              Nuevo Producto
            </Button>
          </Box>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" mb={2}>Productos</Typography>

            <TextField
              label="Buscar por código o nombre…"
              fullWidth
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, width: "50%" }}
            />

            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "#1565C0", color: "white" }}>Código</TableCell>
                    <TableCell sx={{ backgroundColor: "#1565C0", color: "white" }}>Nombre</TableCell>
                    <TableCell sx={{ backgroundColor: "#1565C0", color: "white" }}>PVP1</TableCell>
                    <TableCell sx={{ backgroundColor: "#1565C0", color: "white" }}>IVA</TableCell>
                    <TableCell sx={{ backgroundColor: "#1565C0", color: "white" }}>Stock</TableCell>
                    <TableCell sx={{ backgroundColor: "#1565C0", color: "white" }}>Acción</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((p) => {
                        const color = p.stock <= p.stock_minimo ? "red" : "#333";

                        return (
                          <TableRow key={p.id}>
                            <TableCell>
                              <div>{p.code}</div>
                              {p.barcode && <div style={{ fontSize: "11px", color: "#333" }}>{p.barcode}</div>}
                            </TableCell>

                            <TableCell>{p.name}</TableCell>

                            <TableCell>{Number(p.pvp1).toFixed(2)}</TableCell>

                            <TableCell>
                              <div>{p.iva_porcentaje === 999 ? "No IVA" : `${p.iva_porcentaje}%`}</div>
                              <div style={{ fontSize: "11px", color: "#333" }}>${Number(p.iva_valor).toFixed(2)}</div>
                            </TableCell>

                            <TableCell>
                              <div style={{ color: "#000" }}>{p.stock}</div>
                              <div style={{ fontSize: "11px", color }}>{`Min: ${p.stock_minimo}`}</div>
                            </TableCell>

                            <TableCell sx={{ display: "flex", gap: 1 }}>
                              <Button variant="outlined" size="small" onClick={() => setDetailsProduct(p)} sx={{ textTransform: "none", borderColor: "#1565C0", color: "#1565C0", backgroundColor: "white" }}>
                                Detalles
                              </Button>

                              <Button variant="contained" size="small" onClick={() => openEdit(p)} sx={{ textTransform: "none", backgroundColor: "#1565C0" }}>
                                Editar
                              </Button>

                              <Button variant="contained" color="error" size="small" onClick={() => openDeleteModal(p.id)}>
                                Eliminar
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={page}
              onPageChange={(e, np) => setPage(np)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
          </Paper>
        </Box>
      </Box>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "white", p: 4, borderRadius: 2, width: 350 }}>
          <Typography variant="h6" mb={2}>¿Eliminar producto?</Typography>
          <Typography mb={3}>Esta acción no se puede deshacer.</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>Eliminar</Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "white", p: 4, borderRadius: 2, width: 760, maxHeight: "90vh", overflowY: "auto" }}>
          <EditProduct product={selectedProduct} onClose={() => setEditModalOpen(false)} onSubmit={handleEditSubmit} />
        </Box>
      </Modal>

      <Modal open={!!detailsProduct} onClose={() => setDetailsProduct(null)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "white", p: 4, borderRadius: 2, width: 760, maxHeight: "90vh", overflowY: "auto" }}>
          <ViewProduct product={detailsProduct} />
        </Box>
      </Modal>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProduct;
